import { ColumnProps } from "antd/lib/table";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OrderContext from "../../../context/order/context";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { IRatedOrder } from "../../../models/order/response";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import Tag from "../../general/antd/tag";
import {
  getOrderRate,
  getOrderStatus,
  getOrderStatusColor,
  getOrderStyle,
  getOrderTypeColor,
  getDeliveryModeName,
  getDeliveryModeColor,
} from "../../../models/order/enum";
import moment from "moment";
import styles from './styles.module.scss'
import TripBadge from "../trip-badge";
import Space from "../../general/antd/space";
import DeliveryModeEditButton from "../delivery-mode-edit-button";
import { formatPersonName } from "../../../utils/helpers/person";
interface IProps { }

const OrderRatedTable: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const { listRated, actions, loading, query } = useContext(OrderContext);

  const columns: ColumnProps<IRatedOrder>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 100,
      align: "center",
    },
    {
      title: t("number_id"),
      dataIndex: "number",
      key: "number",
      sorter: {
        multiple: 1,
      },
      width: 100,
      align: "center",
    },
    {
      title: t("trip_info"),
      dataIndex: "tripId",
      key: "tripId",
      align: "center",
      width: 150,
      render: (_, record) => {
        return (
          <TripBadge
            tripId={record?.tripId}
            tripNumber={record?.tripNumber}
            tripStatus={record?.tripStatus}
          />
        );
      },
    },
    {
      title: t("delivery_mode"),
      dataIndex: "deliveryMode",
      key: "deliveryMode",
      align: "center",
      width: 200,
      render: (_, record) => {
        return (
          <Space size="small" align="center">
            <Tag
              title={t(getDeliveryModeName(record?.deliveryMode))}
              color={getDeliveryModeColor(record?.deliveryMode)}
            />
            <DeliveryModeEditButton
              order={record}
              onSuccess={() => actions.getDataRated()}
              size="small"
            />
          </Space>
        );
      },
    },
    {
      title: t("current_order_status"),
      dataIndex: "status",
      align: "center",
      key: "status",
      width: 250,
      render: (_, record) => {
        return (
          <Tag
            title={getOrderStatus(record?.status)}
            color={getOrderStatusColor(record?.status)}
          />
        );
      },
    },
    {
      title: t("client"),
      dataIndex: "client",
      align: "center",
      key: "client",
      width: 200,
      render: (_, record) => {
        return (
          <Tag
            title={formatPersonName(record?.client)}
          />
        );
      },
    },
    {
      title: t("client_mobile"),
      dataIndex: "mobile",
      align: "center",
      key: "mobile",
      width: 200,
      render: (_, record) => {
        return record?.client?.cleanMobile ?? "-";
      },
    },
    {
      title: t("accepted_orders_count"),
      dataIndex: "acceptedOrdersCount",
      align: "center",
      key: "acceptedOrdersCount",
      width: 200,
      render: (_, record) => {
        return record?.client?.acceptedOrdersCount ?? "-";
      },
    },
    {
      title: t("rated"),
      dataIndex: "rating",
      align: "center",
      key: "rating",
      width: 200,
      render: (_, record) => {
        return getOrderRate(record?.rating) ?? "-";
      },
    },
    {
      title: t("final_price"),
      dataIndex: "finalPrice",
      align: "center",
      key: "finalPrice",
      width: 200,
      render: (_, record) => {
        return record?.finalPrice ?? "-";
      },
    },
    {
      title: t("request_sourse_type"),
      dataIndex: "type",
      align: "center",
      key: "type",
      width: 200,
      render: (_, record) => {
        return (
          <Tag
            title={getOrderStyle(record?.type)}
            color={getOrderTypeColor(record?.type)}
          />
        );
      },
    },
    {
      title: t("note"),
      dataIndex: "clientRateNote",
      align: "center",
      key: "mobile",
      width: 200,
      render: (_, record) => {
        return record?.clientRateNote ?? "-";
      },
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      width: 100,
      render: (_, record) => {
        return moment(record?.createdAt).format("YYYY-MM-DD");
      },
    },

  ];

  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        dataSource={listRated?.data ?? []}
        loading={loading.includes("list")}
        size="small"
        total={listRated?.total}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}

        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters
          );
        }}
      />
    </>
  );
};

export default OrderRatedTable;
